import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Card, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

const text = value => value === null || value === undefined || value === '' ? '—' : String(value);
const date = value => value ? new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
}).format(new Date(Number(value))) : '—';
const csv = value => `"${text(value).replace(/"/g, '""')}"`;
const money = value => value === null || value === undefined ? '—' :
  (Number(value) / 100).toFixed(2);
const driver = item => item.assignedDriverSnapshot ?
  [item.assignedDriverSnapshot.firstName, item.assignedDriverSnapshot.lastName]
    .filter(Boolean).join(' ') : item.driver_name;
const vehicle = item => item.assignedVehicleSnapshot ?
  [item.assignedVehicleSnapshot.make, item.assignedVehicleSnapshot.model,
    item.assignedVehicleSnapshot.licensePlate].filter(Boolean).join(' · ') :
  [item.vehicleMake, item.vehicleModel, item.vehicle_number].filter(Boolean).join(' · ');
const schedule = item => item.bookLater ? `Planifiée — ${date(item.tripdate)}` : 'Immédiate';

export default function RegulatoryReports() {
  const bookings = useSelector(state => state.bookinglistdata.bookings);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const rows = useMemo(() => (bookings || []).filter(item => {
    const matchesStatus = status === 'ALL' || item.regulatoryStatus === status;
    const haystack = [item.reference, item.customer_name, item.driver_name,
      item.pickupAddress, item.dropAddress, item.bookingSource].join(' ').toLowerCase();
    return matchesStatus && haystack.includes(search.trim().toLowerCase());
  }), [bookings, search, status]);

  const exportCsv = () => {
    const header = ['Référence', 'Demandée le (Québec)', 'Source', 'Type',
      'État course', 'État réglementaire', 'État paiement', 'Client', 'Chauffeur',
      'Véhicule', 'Départ', 'Destination', 'Prix maximal (CAD)', 'Prix final',
      'Consentement client (Québec)', 'Méthode de consentement', 'Chauffeur avisé le'];
    const body = rows.map(item => [item.reference, date(item.bookingDate),
      item.bookingSource || 'LEGACY', schedule(item), item.status, item.regulatoryStatus,
      item.paymentStatus, item.customer_name, driver(item), vehicle(item), item.pickupAddress,
      item.dropAddress, money(item.maximumPriceCents), item.trip_cost,
      date(item.customerConsentAt), item.consentEvidence?.consentMethod ||
        item.consentEvidence?.action, date(item.driverNotificationAt)]);
    const content = '\uFEFF' + [header, ...body]
      .map(row => row.map(csv).join(',')).join('\r\n');
    const blob = new Blob([content],
      { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rapport-interne-reservations-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2,
      flexWrap: 'wrap', mb: 2 }}>
      <Box><Typography variant="h5" fontWeight={800}>Réservations et conformité</Typography>
        <Typography color="text.secondary">Vue consolidée des voyages et preuves de consentement.</Typography></Box>
      <Button variant="outlined" onClick={exportCsv}>Exporter en CSV</Button>
    </Box>
    <Alert severity="info" sx={{ mb: 2 }}>Rapport administratif interne. Il ne remplace pas le rapport technologique officiel destiné à la CTQ.</Alert>
    <Card sx={{ p: 2, mb: 2 }}><Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <TextField sx={{ flex: 1, minWidth: 260 }} label="Rechercher" value={search}
        onChange={event => setSearch(event.target.value)} />
      <TextField select label="État réglementaire" value={status}
        onChange={event => setStatus(event.target.value)} sx={{ minWidth: 220 }}>
        {['ALL', 'DRAFT', 'QUOTED', 'AWAITING_CUSTOMER_CONSENT',
          'CUSTOMER_CONSENTED', 'DISPATCHABLE', 'DRIVER_NOTIFIED',
          'DRIVER_ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'PAID'].map(value =>
          <MenuItem key={value} value={value}>{value === 'ALL' ? 'Tous' : value}</MenuItem>)}
      </TextField>
    </Box></Card>
    <Card><TableContainer><Table size="small"><TableHead><TableRow>
      {['Référence', 'Date', 'Source', 'Type', 'Course', 'Réglementaire', 'Paiement',
        'Chauffeur', 'Véhicule', 'Départ', 'Destination', 'Prix max.', 'Consentement'].map(label =>
        <TableCell key={label}>{label}</TableCell>)}
    </TableRow></TableHead><TableBody>{rows.map((item, index) =>
      <TableRow key={item.id || item.reference || index} hover>
        <TableCell>{text(item.reference)}</TableCell><TableCell>{date(item.bookingDate)}</TableCell>
        <TableCell>{text(item.bookingSource || 'LEGACY')}</TableCell><TableCell>{schedule(item)}</TableCell><TableCell>{text(item.status)}</TableCell>
        <TableCell>{text(item.regulatoryStatus)}</TableCell><TableCell>{text(item.paymentStatus)}</TableCell>
        <TableCell>{text(driver(item))}</TableCell><TableCell>{text(vehicle(item))}</TableCell><TableCell>{text(item.pickupAddress)}</TableCell>
        <TableCell>{text(item.dropAddress)}</TableCell><TableCell>{item.maximumPriceCents === undefined ? '—' : `${(Number(item.maximumPriceCents) / 100).toFixed(2)} $`}</TableCell>
        <TableCell>{date(item.customerConsentAt)}</TableCell>
      </TableRow>)}
      {!rows.length && <TableRow><TableCell colSpan={13}>Aucune réservation correspondante.</TableCell></TableRow>}
    </TableBody></Table></TableContainer></Card>
  </Box>;
}
